import { useEffect, useRef } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import {
  AppointmentStatus,
  ApprovalFeeOutcome,
  getAppointmentStatusLabel,
  getApprovalFeeOutcomeColor,
  getApprovalFeeOutcomeLabel,
} from '@/api/enums'
import { Badge } from '@/components/ui/Badge'
import { StatusBadge } from '@/components/ui/StatusBadge'
import {
  formatAppointmentTime as formatTimeSlot,
  formatDisplayDate,
  formatLongDate,
  formatUtcTime,
} from '@/utils/date'
import type { AppointmentDashboardResponse } from '@/api/contracts/appointments.contract'

type AppointmentsTableProps = {
  appointments: AppointmentDashboardResponse[]
  onRowClick: (id: string) => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}

const statusClass = (status: AppointmentStatus) => {
  if (status === AppointmentStatus.Completed) return 'text-[var(--color-status-success)]'
  if (status === AppointmentStatus.Canceled || status === AppointmentStatus.NoShow)
    return 'text-[var(--color-status-error)]'
  return 'text-foreground'
}


const OUTCOME_CLASSES: Record<string, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-rose-50 text-rose-700 border-rose-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  neutral: 'bg-muted text-muted-foreground border-border',
}

/**
 * What happened to the $50. Blank until there is an outcome, so a live
 * appointment does not carry a badge that says nothing.
 *
 * `Refund owed` is the row an admin has to act on: the refund failed and the
 * patient is still out of pocket until somebody puts it through in Stripe.
 */
const FeeOutcomeCell = ({ appointment }: { appointment: AppointmentDashboardResponse }) => {
  const outcome = appointment.approvalFeeOutcome
  if (outcome === ApprovalFeeOutcome.None) return <span className='text-muted-foreground'>—</span>

  return (
    <Badge
      className={`text-xs font-medium border ${OUTCOME_CLASSES[getApprovalFeeOutcomeColor(outcome)]}`}
      title={appointment.refundFailureReason ?? undefined}
    >
      {getApprovalFeeOutcomeLabel(outcome)}
    </Badge>
  )
}

export function AppointmentsTable({ appointments, onRowClick, hasNextPage, isFetchingNextPage, onLoadMore }: AppointmentsTableProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) onLoadMore()
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  return (
    <>
      <p className='text-sm text-muted-foreground'>{appointments.length} appointments</p>

      <div className='bg-card'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Patient Name</TableHead>
              <TableHead>Specialist</TableHead>
              <TableHead>Request Created</TableHead>
              <TableHead>Appointment Date</TableHead>
              <TableHead>Appointment Status</TableHead>
              <TableHead>Approval Charge</TableHead>
              <TableHead>Completion Charge</TableHead>
              <TableHead>Approval Fee</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.map((appt) => {
              return (
                <TableRow
                  key={appt.id}
                  className='cursor-pointer hover:bg-muted/50'
                  onClick={() => onRowClick(appt.id)}
                  tabIndex={0}
                  role='button'
                  aria-label={`View appointment for ${appt.userName || 'patient'}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(appt.id)
                    }
                  }}
                >
                  <TableCell className='font-medium'>{appt.userName || 'Unknown Patient'}</TableCell>
                  <TableCell>{appt.specialistName || 'Unknown Specialist'}</TableCell>
                  <TableCell className='text-sm text-muted-foreground whitespace-nowrap'>
                    {appt.requestedTime
                      ? `${formatDisplayDate(appt.requestedTime)} at ${formatUtcTime(appt.requestedTime)}`
                      : '—'}
                  </TableCell>
                  <TableCell>{formatDisplayDate(appt.startTime)} at {formatTimeSlot(appt.startTime)}</TableCell>
                  <TableCell>
                    <span className={`text-sm ${statusClass(appt.status)}`}>
                      {getAppointmentStatusLabel(appt.status)}
                    </span>
                  </TableCell>
                  <TableCell
                    title={
                      appt.approvalChargedOnUtc
                        ? `Charged ${formatLongDate(appt.approvalChargedOnUtc)}`
                        : undefined
                    }
                  >
                    <StatusBadge type='charge' status={appt.approvalPaymentStatus} />
                  </TableCell>
                  <TableCell
                    title={
                      appt.completionChargedOnUtc
                        ? `Charged ${formatLongDate(appt.completionChargedOnUtc)}`
                        : undefined
                    }
                  >
                    <StatusBadge type='charge' status={appt.completionPaymentStatus} />
                  </TableCell>
                  <TableCell>
                    <FeeOutcomeCell appointment={appt} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {appointments.length === 0 && (
        <div className='py-8 text-center text-muted-foreground'>
          <p>No appointments found matching your search.</p>
        </div>
      )}

      <div ref={sentinelRef} className='h-1' />
      {isFetchingNextPage && (
        <div className='py-4 text-center text-sm text-muted-foreground'>Loading more...</div>
      )}
    </>
  )
}
